import { Mail, Phone, MoreVertical, Edit, Trash2, Archive, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ContactDetail } from '@/lib/api';
import { formatDate } from './utils';

export function ContactCard({
  contact,
  onEdit,
  onDelete,
  onArchive,
  onRestore,
  onPermanentDelete,
  isTrashView,
}: {
  contact: ContactDetail;
  onEdit: (contact: ContactDetail) => void;
  onDelete: (contact: ContactDetail) => void;
  onArchive: (contact: ContactDetail) => void;
  onRestore?: (contact: ContactDetail) => void;
  onPermanentDelete?: (contact: ContactDetail) => void;
  isTrashView?: boolean;
}) {
  return (
    <Card
      className="group hover:shadow-md transition-shadow cursor-pointer"
      role="button"
      tabIndex={0}
      aria-label={`Contact: ${[contact.firstName, contact.lastName].filter(Boolean).join(' ')}`}
      onClick={() => !isTrashView && onEdit(contact)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !isTrashView) onEdit(contact);
      }}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {contact.priorityColor && (
                <span
                  className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: contact.priorityColor }}
                  title={contact.priorityName || 'Priority'}
                />
              )}
              <h3 className="font-semibold truncate">
                {[contact.firstName, contact.lastName].filter(Boolean).join(' ')}
              </h3>
              {contact.categoryName && (
                <Badge
                  variant="secondary"
                  className="text-xs flex-shrink-0"
                  style={{
                    backgroundColor: contact.categoryColor
                      ? `${contact.categoryColor}20`
                      : undefined,
                    color: contact.categoryColor || undefined,
                    borderColor: contact.categoryColor ? `${contact.categoryColor}40` : undefined,
                  }}
                >
                  {contact.categoryName}
                </Badge>
              )}
            </div>

            {(contact.title || contact.company) && (
              <p className="text-sm text-muted-foreground mt-0.5 truncate">
                {contact.title}
                {contact.title && contact.company && ' at '}
                {contact.company}
              </p>
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
              {contact.emails.length > 0 && (
                <span className="flex items-center gap-1 truncate">
                  <Mail className="h-3 w-3 flex-shrink-0" />
                  {contact.emails[0].value}
                  {contact.emails.length > 1 && (
                    <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-0.5">
                      +{contact.emails.length - 1}
                    </Badge>
                  )}
                </span>
              )}
              {contact.phones.length > 0 && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3 flex-shrink-0" />
                  {contact.phones[0].value}
                  {contact.phones.length > 1 && (
                    <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-0.5">
                      +{contact.phones.length - 1}
                    </Badge>
                  )}
                </span>
              )}
            </div>

            {contact.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {contact.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            <p className="text-xs text-muted-foreground mt-2">
              Gathered {formatDate(contact.gatheredAt)}
              {contact.source && ` via ${contact.source}`}
            </p>
          </div>

          {isTrashView ? (
            <div className="flex gap-1 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRestore?.(contact);
                }}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Restore
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onPermanentDelete?.(contact);
                }}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Delete Forever
              </Button>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(contact)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onArchive(contact)}>
                  <Archive className="h-4 w-4 mr-2" />
                  {contact.status === 'archived' ? 'Unarchive' : 'Archive'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(contact);
                  }}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
